<template>
	<nav data-component="menu" class="menu-container">
		<div class="content">
			<span class="bg-menu" />

			<nuxt-link to="/" aria-label="Home" class="home-link" @touchend.prevent="onLogoTouchEnd">
				<div class="logo-container" @mouseenter="onLogoMouseEnter">
					<RiveAsset ref="riveRef" class="logo-rive" v-bind="riveProps" />
				</div>
			</nuxt-link>

			<div class="ctas">
				<div v-if="mainItems.length" class="menu-items main-items">
					<Button v-for="(menuItem, i) in mainItems" :key="`main-${menuItem?.label || i}`" data-tone="secondary" v-bind="menuItem" size="medium" />
				</div>
				<div v-if="secondaryItems.length" class="menu-items secondary-items">
					<Button v-for="(menuItem, i) in secondaryItems" :key="`secondary-${menuItem?.label || i}`" data-tone="primary" v-bind="menuItem" size="medium" />
				</div>
			</div>
		</div>
	</nav>
</template>

<script setup>
import { RiveAsset } from '~/components/core'

import { useEmitter } from '~/composables/core'
import { Button } from '~/components/primitives'

// import.meta.client && gsap.registerPlugin(ScrollTrigger)

const props = defineProps({
	externalLinks: { type: Array, default: undefined },
	main: { type: Array, default: undefined },
	secondary: { type: Array, default: undefined },
	rive: { type: [Boolean, Object], default: false },
})

const riveRef = ref(null)
const route = useRoute()
const router = useRouter()
const emitter = useEmitter()
let offPageHeaderAnimationStart = null
let lastTriggeredRouteKey = null

const mainItems = computed(() => (Array.isArray(props.main) ? props.main.filter(Boolean) : []))
const secondaryItems = computed(() => (Array.isArray(props.secondary) ? props.secondary.filter(Boolean) : []))
const riveProps = computed(() => {
	const overrideProps = props.rive && typeof props.rive === 'object' ? props.rive : {}

	return {
		asset: { file: '/silkriver_logo.riv' },
		layout: 'cover',
		stateMachine: 'State Machine 1',
		...overrideProps,
	}
})

const playRive = () => {
	const instance = riveRef.value
	if (!instance) return

	const play = () => {
		// Ensure the state machine is running before pulsing the input.
		instance.play?.()

		if (typeof instance.setRiveInput !== 'function') return
		if (!instance.setRiveInput('logoHover', 0)) return

		requestAnimationFrame(() => {
			instance.setRiveInput('logoHover', 1)

			window.setTimeout(() => {
				instance.setRiveInput('logoHover', 0)
			}, 750)
		})
	}
	const loaded = instance.loaded?.()

	if (loaded && typeof loaded.then === 'function') {
		loaded.then(play)
		return
	}

	play()
}

const onLogoMouseEnter = () => playRive()
const navigateHome = () => {
	if (route.path === '/') return
	router.push('/')
}
const onLogoTouchEnd = () => navigateHome()

const getCurrentRouteKey = () => route.fullPath || route.path || ''
const resolveRouteKeyFromPayload = (payload) => {
	if (payload && typeof payload === 'object' && typeof payload.routeKey === 'string' && payload.routeKey.length > 0) return payload.routeKey
	return getCurrentRouteKey()
}
const handlePageHeaderAnimationStart = (payload) => {
	const routeKey = resolveRouteKeyFromPayload(payload)
	if (routeKey && routeKey === lastTriggeredRouteKey) return

	lastTriggeredRouteKey = routeKey
	playRive()
}

onMounted(() => {
	offPageHeaderAnimationStart = emitter.on('page-header-animation-start', handlePageHeaderAnimationStart)
})

onBeforeUnmount(() => {
	offPageHeaderAnimationStart?.()
	offPageHeaderAnimationStart = null
})
</script>

<style lang="scss" scoped>
[data-component='menu'] {
	position: fixed;
	top: 0;
	left: 0;

	z-index: 11;

	pointer-events: none;

	padding: rs(48, 28) var(--grid-margin);

	transition: all 0.5s ease(out-quint);

	display: flex;
	align-items: center;
	justify-content: flex-start;
	width: 100%;
	z-index: 10;

	@include max-width($sm) {
		justify-content: center;
	}

	.content {
		pointer-events: auto;

		position: relative;
		display: flex;
		flex-direction: row;
		gap: rs(28, 28);

		padding: rs(6, 6) rs(6, 6) rs(6, 6) rs(18, 18);

		.bg-menu {
			position: absolute;
			inset: 0;

			z-index: -1;

			border-radius: rs(15, 15);

			background-color: var(--color-river-blue-25, var(--color-geral-white));

			transition: background-color 0.5s ease(out-quint);

			border-top: 1px solid var(--color-geral-white, var(--color-geral-white));
			border-bottom: 1px solid var(--color-river-blue-100, var(--color-geral-white));

			box-shadow: 0 -1px 0 0 #d3e2fa inset, 0 1px 0 0 #fff inset, 0 71px 20px 0 rgba(86, 123, 210, 0), 0 45px 18px 0 rgba(86, 123, 210, 0.01), 0 26px 15px 0 rgba(86, 123, 210, 0.05),
				0 11px 11px 0 rgba(86, 123, 210, 0.09), 0 3px 6px 0 rgba(86, 123, 210, 0.1);
		}

		.home-link {
			display: flex;
			justify-content: center;
			align-items: center;
		}

		.logo-container {
			position: relative;

			display: flex;
			align-items: center;
			width: rs(110, 110);
			height: rs(20, 20);

			.logo-rive {
				display: block;
				z-index: 0;
				width: 100%;
				height: 100%;
				pointer-events: none;

				:deep(canvas) {
					pointer-events: none;
				}
			}
		}

		.ctas {
			display: flex;
			align-items: center;
			gap: rs(10, 10);
		}

		.menu-items {
			display: flex;
			align-items: center;
			gap: rs(10, 10);
		}
	}
}
</style>
